import {
  Actions,
  Button,
  closeCurrentDialog,
  DropdownActionMenuItem,
  FormSubmitHandler,
  openConfirmDialog,
  openDialog,
  showNotification,
} from 'platform/components';
import {HStack} from 'platform/foundation';
import styled from 'styled-components';

import {isNotNil} from 'ramda';

import {
  useSourcingDeletePresetMutation,
  useSourcingMarkPresetAllSeenMutation,
  useSourcingUpdatePresetMutation,
} from '@dms/api/core';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {handleApiError} from '@dms/shared';

import {buildArray, TestIdProps} from 'shared/web';

import {useIsSourcingCardViewEnforced} from '../../../../hooks/useIsSourcingCardViewEnforced';
import {FilterPresetForm, SourcingFilterPresetFormData} from '../FilterPresetForm/FilterPresetForm';

export interface FilterPresetButtonProps extends TestIdProps {
  id: string;
  title: string;
  count: number | null;
  isSelected?: boolean;
  isNotificationsMuted: boolean | null;
  onClick?: () => void;
  onResetFilterPreset?: () => void;
}

export function FilterPresetButton(props: FilterPresetButtonProps) {
  const isCardViewEnforced = useIsSourcingCardViewEnforced();
  const [updatePreset] = useSourcingUpdatePresetMutation();
  const [deletePreset] = useSourcingDeletePresetMutation();
  const [markPresetAllSeen] = useSourcingMarkPresetAllSeenMutation();

  const hasUnreadOffers = isNotNil(props.count) && props.count > 0;

  const handleRename = () => {
    const onSubmit: FormSubmitHandler<SourcingFilterPresetFormData> = async (values) =>
      await updatePreset({
        id: props.id,
        updatePresetRequestBody: {
          name: values.name,
          allowNotifications: values.allowNotifications ?? undefined,
        },
      })
        .unwrap()
        .then(closeCurrentDialog)
        .catch(handleApiError);

    openDialog(
      <FilterPresetForm
        initialValues={{
          name: props.title,
          allowNotifications: isNotNil(props.isNotificationsMuted)
            ? !props.isNotificationsMuted
            : null,
        }}
        onSubmit={onSubmit}
      />,
      {
        title: i18n.t('entity.sourcing.filterPresets.labels.rename'),
      }
    );
  };

  const handleEnableNotifications = () => {
    updatePreset({
      id: props.id,
      updatePresetRequestBody: {
        allowNotifications: true,
      },
    })
      .unwrap()
      .catch(handleApiError);
  };

  const handleDisableNotifications = () => {
    updatePreset({
      id: props.id,
      updatePresetRequestBody: {
        allowNotifications: false,
      },
    })
      .unwrap()
      .catch(handleApiError);
  };

  const handleMarkAllAsRead = () => {
    markPresetAllSeen({id: props.id})
      .unwrap()
      .then(() =>
        showNotification.success(
          i18n.t('entity.sourcing.filterPresets.notifications.allMarkedAsRead')
        )
      )
      .catch(handleApiError);
  };

  const handleDelete = () => {
    openConfirmDialog({
      text: i18n.t('entity.sourcing.filterPresets.actions.confirmDelete'),
      onConfirm: () => {
        deletePreset({id: props.id}).unwrap().then(props.onResetFilterPreset).catch(handleApiError);
      },
    });
  };

  return (
    <HStack align="center" minWidth={0} maxWidth="100%">
      <ButtonGroup>
        <Button
          title={props.title}
          wrap={isCardViewEnforced}
          badge={hasUnreadOffers ? {value: `+${props.count}`, colorScheme: 'green'} : undefined}
          rightIcon={props.isNotificationsMuted ? 'social/notifications_off' : undefined}
          onClick={props.onClick}
          variant={props.isSelected ? 'primary' : 'outlined'}
          data-testid={
            props['data-testid'] ??
            testIds.sourcing.classifieds(`filterPresetButtons-${props.title}`)
          }
        />
        <Actions
          actions={[
            {
              type: 'dropdown-iconButton',
              priority: props.isSelected ? 'primary' : 'tertiary',
              icon: 'navigation/more_vert',
              menuItems: buildArray<DropdownActionMenuItem>()
                .when(hasUnreadOffers, {
                  label: i18n.t('entity.sourcing.filterPresets.actions.markAllAsRead'),
                  leftIcon: 'action/done_all',
                  onClick: handleMarkAllAsRead,
                })
                .when(isNotNil(props.isNotificationsMuted) && props.isNotificationsMuted, {
                  label: i18n.t('entity.sourcing.filterPresets.actions.enableNotifications'),
                  leftIcon: 'social/notifications',
                  onClick: handleEnableNotifications,
                })
                .when(isNotNil(props.isNotificationsMuted) && !props.isNotificationsMuted, {
                  label: i18n.t('entity.sourcing.filterPresets.actions.disableNotifications'),
                  leftIcon: 'social/notifications_off',
                  onClick: handleDisableNotifications,
                })
                .add({
                  label: i18n.t('entity.sourcing.filterPresets.actions.rename'),
                  leftIcon: 'image/edit',
                  onClick: handleRename,
                })
                .add({
                  label: i18n.t('entity.sourcing.filterPresets.actions.delete'),
                  leftIcon: 'action/delete',
                  severity: 'danger',
                  onClick: handleDelete,
                }),
            },
          ]}
          data-testid={testIds.sourcing.classifieds('filterPresetButtons-more')}
        />
      </ButtonGroup>
    </HStack>
  );
}

// Hack for non-standard component
// eslint-disable-next-line eag/no-css-property
const ButtonGroup = styled.div`
  display: flex;
  align-items: stretch;
  max-width: 100%;

  /* First button (direct child): zero radius on right side, only when multiple children */
  > button:first-child:not(:only-child) {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
    border-right: 0;
  }

  /* Middle buttons: all radiuses 0 */
  > button:not(:first-child):not(:last-child) {
    border-radius: 0;
    border-right: 0;
  }

  /* Last button (nested in last child): zero radius on left side */
  > *:last-child button {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }

  /*
   * Actions renders HStack(align=center) > span > IconButton, and IconButton has a hard 32px
   * height. In card view the title Button wraps to several lines (height: auto), so the menu
   * would sit shorter than it. Stretch the whole chain to the group's height instead.
   */
  > *:last-child > span {
    display: flex;
    align-self: stretch;
  }

  > *:last-child button {
    height: 100%;
  }
`;
